import { Option } from "@inrev/core";
import {
	bandWidthPct,
	FICO_MAX,
	FICO_MIN,
	formatReportDate,
	pointsToNextBand,
	SCORE_BANDS,
	scoreBand,
	scorePositionPct,
} from "@portal/api/account/admin/individualCreditReport";
import { cn } from "@portal/components/lib/utils";
import { Icon, Text } from "@portal/components/primitives";
import type { FC, ReactNode } from "react";
import { Fact, StatusSummary } from "./ReportSection";

// Matches the commercial report's Experian FSR number.
const BIG_NUMBER = "text-[64px] leading-none font-semibold";

type ScoreHeroProps = {
	bureau: string;
	score: number | undefined;
	model: string | undefined;
	/** Shown beside the score: range bar, notes. */
	aside?: ReactNode;
	facts: ReactNode;
};

/** The shared top block: big score on the left, facts on the right. */
export const ScoreHero: FC<ScoreHeroProps> = ({ bureau, score, model, aside, facts }) => {
	const band = Option.isSome(score) ? scoreBand(score) : undefined;
	return (
		<div
			className="grid grid-cols-1 items-start gap-6 md:grid-cols-[220px_minmax(0,1fr)]"
			data-testid="individual_credit_score_hero"
		>
			<div className="flex flex-col gap-5">
				<Text type="eyebrow" color="tertiary">
					{bureau} credit score
				</Text>
				<div className="flex flex-col gap-3">
					{Option.isSome(score) && Option.isSome(band) ? (
						<>
							<div className="flex items-baseline gap-2">
								<Text
									type="display"
									color={band.tone}
									className={BIG_NUMBER}
									data-testid="individual_credit_score"
								>
									{score}
								</Text>
								<Text type="body" color="tertiary">
									/ {FICO_MAX}
								</Text>
							</div>
							<StatusSummary tone={band.tone} testId="individual_credit_score_band">
								{band.name}
							</StatusSummary>
						</>
					) : (
						<Text type="display" color="subtle" className={BIG_NUMBER}>
							--
						</Text>
					)}
					{Option.isSome(model) && (
						<Text type="caption" color="tertiary">
							Model: {model}
						</Text>
					)}
				</div>
			</div>
			<div className="flex flex-col gap-4">
				{aside}
				{facts}
			</div>
		</div>
	);
};

export const ScoreRangeBar: FC<{ score: number }> = ({ score }) => {
	const current = scoreBand(score);
	const nextBandNote = pointsToNextBand(score);
	return (
		<div
			className="flex flex-col gap-2.5 border-b border-bc-subtle pb-4"
			data-testid="individual_credit_score_range"
		>
			<div className="flex items-baseline justify-between">
				<Text type="eyebrow" color="tertiary">
					Score range
				</Text>
				{Option.isSome(nextBandNote) && (
					<Text type="caption" color="secondary">
						{nextBandNote}
					</Text>
				)}
			</div>
			<div className="relative pt-[30px]">
				<div
					className="absolute top-0 flex -translate-x-1/2 flex-col items-center gap-0.5"
					style={{ left: `${scorePositionPct(score)}%` }}
					aria-hidden
				>
					<div className="flex h-[18px] items-center rounded-md border border-bc-strong bg-surface-base px-1.5">
						<Text type="caption" color="primary" className="font-semibold">
							{score}
						</Text>
					</div>
					<span className="h-[26px] w-0.5 bg-surface-primary" />
				</div>
				<div
					className="flex h-3 gap-[3px]"
					role="img"
					aria-label={`${score} on a ${FICO_MIN} to ${FICO_MAX} scale, ${current.name}`}
				>
					{SCORE_BANDS.map((band) => (
						<span
							key={band.name}
							className={cn("rounded-[2px] first:rounded-l-md last:rounded-r-md", band.chartToken)}
							style={{ flex: `${bandWidthPct(band)} 1 0` }}
						/>
					))}
				</div>
				<div className="mt-2 flex gap-[3px]">
					{SCORE_BANDS.map((band, index) => (
						<div
							key={band.name}
							className={cn(
								"flex",
								index === 0 ? "justify-between" : "justify-center",
								index === SCORE_BANDS.length - 1 && "justify-end",
							)}
							style={{ flex: `${bandWidthPct(band)} 1 0` }}
						>
							{index === 0 && (
								<Text type="caption" color="tertiary">
									{FICO_MIN}
								</Text>
							)}
							<Text
								type="caption"
								color={band === current ? band.tone : "tertiary"}
								className={cn(band === current && "font-semibold")}
							>
								{index === SCORE_BANDS.length - 1 && band !== current ? FICO_MAX : band.name}
							</Text>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

/** Every value sits in an icon-high row so values with and without an icon line up. */
export const FactValue: FC<{ children: ReactNode }> = ({ children }) => (
	<div className="flex h-5 items-center gap-1.5">{children}</div>
);

type PullFactsProps = {
	bankruptcy: boolean | undefined;
	pulledAt: string;
	reusedUntil: string;
};

export const SuccessfulPullFacts: FC<PullFactsProps> = ({ bankruptcy, pulledAt, reusedUntil }) => (
	<div className="grid grid-cols-3 gap-4">
		<Fact label="Bankruptcy" testId="individual_credit_fact_bankruptcy">
			<FactValue>
				<Text type="title" color={bankruptcy ? "danger" : "primary"}>
					{Option.isNone(bankruptcy) ? "--" : bankruptcy ? "Yes" : "No"}
				</Text>
			</FactValue>
		</Fact>
		<Fact label="Date Pulled">
			<FactValue>
				<Icon type="check-circle" size="small" color="confirm" aria-label="Pull successful" />
				<Text type="title" color="primary">
					{formatReportDate(pulledAt)}
				</Text>
			</FactValue>
		</Fact>
		<Fact label="Reused Until" testId="individual_credit_fact_reused_until">
			<FactValue>
				<Text type="title" color="primary">
					{formatReportDate(reusedUntil)}
				</Text>
			</FactValue>
		</Fact>
	</div>
);
